import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import * as Sentry from '@sentry/react';
import { platformService } from './platform';

/**
 * Manual OTA live-update check (Capgo, manual mode).
 * Contract: docs/releases/ios-ota-releases.md · docs/releases/frontend-products.md
 *
 * On a native production cold start we GET a static manifest published by CI:
 *   https://update.hallouminati.app/<product>/<native_version>/latest.json
 * scoped by the product (each native flavour runs its own bundle — see
 * src/product/index.ts) and by the installed IPA's CFBundleShortVersionString,
 * so a device never downloads a web bundle built against a different native
 * version (which might call a Capacitor plugin the installed IPA doesn't have).
 *
 * The product comes from the bundle when it was built with VITE_APP, else from
 * the native bundle id — which is how a legacy universal bundle (published to
 * the old, product-less `/<native_version>/latest.json`) migrates an install
 * onto its product bundle the first time it runs this code.
 *
 * If the manifest advertises a version we aren't already running, we download it
 * and stage it with next() — it activates on the NEXT cold start, never mid-session.
 * App.tsx calls CapacitorUpdater.notifyAppReady() after mount; if a staged bundle
 * fails to boot, Capgo's watchdog auto-reverts to the previous good bundle.
 *
 * Disabled on web/Telegram and on dev builds (build:dev → import.meta.env.PROD false),
 * so a developer's locally-built dist is never replaced — which takes the active
 * reclaim below, not just skipping the check. See reclaimBuiltin().
 *
 * Every native launch, dev or production, first asks whether the builtin bundle
 * is NEWER than the bundle currently running and, if so, resets to it. That is
 * what makes a locally-built production IPA run its own JS on a handset that
 * had previously downloaded an OTA bundle — otherwise Capgo keeps serving the
 * stale download and "already on latest" is the only thing you see.
 */

const OTA_BASE_URL = 'https://update.hallouminati.app';

interface OtaManifest {
    version: string;
    url: string;
    checksum?: string;
    /** vite build epoch (ms) of the bundle, from its dist/build.json — CI ≥ 2026-09. */
    builtAt?: number;
}

export async function checkForLiveUpdate(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    if (await reclaimBuiltin({ onlyIfNewer: import.meta.env.PROD })) return;
    if (!import.meta.env.PROD) return;

    // Tracks which step we're on so a failure logs something useful — Capacitor
    // plugin/fetch errors serialise to "{}" in the console otherwise.
    let phase = 'init';
    try {
        phase = 'current';
        const { bundle } = await CapacitorUpdater.current();
        // NOT CapacitorUpdater.current().native: capacitor.config.ts sets Capgo's
        // `version` to the builtin build epoch (see reclaimBuiltin), and Capgo
        // reports that same value as `native`. The manifest path is keyed by the
        // marketing version CI publishes under, which @capacitor/app still reads
        // straight from CFBundleShortVersionString / Android versionName.
        const { version: native } = await CapacitorApp.getInfo();
        const product = await platformService.getAppType();

        const manifestUrl = `${OTA_BASE_URL}/${product}/${native}/latest.json`;
        phase = `fetch-manifest (${manifestUrl})`;
        const res = await fetch(manifestUrl, { cache: 'no-store' });
        // 404 = no OTA published for this product + native version yet — expected, not an error.
        if (!res.ok) {
            console.info(`[liveUpdate] no manifest for ${product} native ${native} (HTTP ${res.status})`);
            return;
        }

        const manifest = (await res.json()) as OtaManifest;
        if (!manifest?.version || !manifest?.url) return;

        // Already running the advertised bundle — nothing to do.
        if (manifest.version === bundle.version) {
            console.info(`[liveUpdate] already on latest bundle ${manifest.version}`);
            return;
        }

        // Never step DOWN. A locally-built production IPA is newer than whatever
        // CI last published; staging the server's bundle over it would run old JS
        // on next launch — old enough to lack the reclaim above, so it would stick.
        // The manifest carries the bundle's build epoch; ours is __BUILD_EPOCH__.
        // While running the builtin, a manifest without one can't be shown newer,
        // so it is skipped too (CI publishes builtAt; only pre-2026-09 manifests lack it).
        if (typeof manifest.builtAt === 'number' ? manifest.builtAt <= __BUILD_EPOCH__ : bundle.id === 'builtin') {
            console.info(`[liveUpdate] manifest ${manifest.version} is not newer than running bundle — skipping`);
            return;
        }

        // Skip re-downloading a version we've already staged for the next launch.
        phase = 'list';
        const { bundles } = await CapacitorUpdater.list();
        const existing = bundles.find((b) => b.version === manifest.version);

        let staged = existing;
        if (!staged) {
            phase = `download (${manifest.url})`;
            staged = await CapacitorUpdater.download({
                url: manifest.url,
                version: manifest.version,
                checksum: manifest.checksum,
            });
        }
        phase = 'next';
        await CapacitorUpdater.next({ id: staged.id });
        console.info(`[liveUpdate] staged bundle ${manifest.version} for next launch`);
    } catch (error) {
        // Background task — never blocks the UI. Log so we can monitor OTA health.
        const message = error instanceof Error ? error.message : JSON.stringify(error);
        console.warn(`[liveUpdate] failed at "${phase}": ${message}`);
        Sentry.withScope((scope) => {
            scope.setTag('feature', 'ota_live_update');
            scope.setTag('ota_phase', phase);
            Sentry.captureException(error instanceof Error ? error : new Error(`[liveUpdate] ${phase}: ${message}`));
        });
    }
}

/**
 * A dev build runs the JavaScript that is INSIDE it.
 *
 * That is what the header promises and it was not true. Capgo picks the active
 * bundle NATIVELY, at launch, before a line of this file runs, and keeps that
 * choice until something changes it. Nothing here did: `resetWhenUpdate` is
 * false in capacitor.config.ts, and a local rebuild at the same versionCode
 * would not have fired it anyway. So a handset that had ever run a production
 * build kept serving the bundle that build downloaded, and installing a :dev
 * build over it changed nothing — the fresh dist sat unused inside the app
 * while months-old JS ran, and the check that would have replaced it is the one
 * thing a dev build never does.
 *
 * NOT skipping the check: skipping it is what caused this. Reclaiming is an
 * action, and it has to be taken.
 *
 * It presents as a native change that visibly registers and then does nothing,
 * because the JavaScript that would call it is old. The tell is one line —
 * `adb logcat -d | grep "assets/index-"` against what is in
 * android/app/src/main/assets/public/assets/.
 *
 * reset() reloads the webview, which on the one launch that needs it is the
 * right trade: every launch after it is already `builtin` and costs a current()
 * call.
 *
 * A PRODUCTION build (`pnpm build:<product>` installed from Xcode / TestFlight)
 * has the same problem with a twist: it must keep a downloaded bundle that is
 * newer than its builtin, so it can't blindly reset. It resets `onlyIfNewer`:
 * when the builtin's build epoch (Capgo `version`, stamped by capacitor.config.ts)
 * is later than the running bundle's __BUILD_EPOCH__. Returns true when a reset
 * was issued (the caller must stop — the page is going away).
 */
async function reclaimBuiltin({ onlyIfNewer }: { onlyIfNewer: boolean }): Promise<boolean> {
    try {
        const { bundle } = await CapacitorUpdater.current();
        if (bundle.id === 'builtin') return false;

        if (onlyIfNewer) {
            // Production: only step back to the builtin when it was built after
            // the running bundle. Capgo's builtin `version` is the vite build epoch
            // capacitor.config.ts stamped at `cap sync` (Date.now() ms); a native
            // shell synced before that existed reports a semver we ignore. The
            // running bundle's own epoch is __BUILD_EPOCH__ — this code IS the
            // running bundle, so an OTA download older than the IPA it sits in
            // steps aside here.
            const { version } = await CapacitorUpdater.getBuiltinVersion();
            const builtinAt = Number(version);
            if (!Number.isFinite(builtinAt) || builtinAt <= __BUILD_EPOCH__) return false;
            console.info(`[liveUpdate] builtin (${new Date(builtinAt).toISOString()}) is newer than bundle ${bundle.version} — resetting to builtin`);
        } else {
            console.info(`[liveUpdate] dev build — dropping bundle ${bundle.version} for builtin`);
        }
        // Never resolves: the reload takes the JavaScript context with it.
        await CapacitorUpdater.reset();
        return true;
    } catch (error) {
        // Not user-facing and not actionable: the cost of failing here is the
        // stale bundle we already had.
        console.warn('[liveUpdate] reclaimBuiltin failed:', error);
        return false;
    }
}
